import type {
  AccountDetail,
  AllocationConstraintType,
  AverageValueConstraintType,
  BuyUniverseConstraintType,
  CashflowBandConstraintType,
  CashflowTarget,
  DiversificationConstraintType,
  LadderPolicyConstraintType,
  ObjectiveStrategy,
  OrderLimitConstraintType,
  PiecewiseConstraintType,
  ProposalAction,
  ProposalOrder,
  ProposalStatus,
  TaxProfile as TaxProfileValue,
  TradeConstraintType,
  UniverseConstraintType,
  ValuationConfigBasis,
} from '@moment/horizon-data-access/proposal'
import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { BondType } from '@moment/trading-types/reference-data'
import type { ConstraintRelaxation } from '@/legacy-api/proposals-slice-types'
import type { EquationToken } from '@/portfolio/proposal-builder/objective-function/equation-tokens'
import type { UseProposalBuilderColumnConfigReturn } from '@/portfolio/proposal-builder/table/hooks/use-proposal-builder-column-config'
import type { useProposalTableState } from '@/portfolio/proposal-builder/table/hooks/use-proposal-table-state'
import type { ComplianceCheck } from '@/types/api/compliance'
import type {
  AllocationConstraint,
  AverageValueConstraint,
  DiversificationConstraint,
  LadderPolicyConstraint,
  ModelConstraint,
  PiecewiseConstraint,
  UniverseConstraint,
} from '@/types/api/constraints'
import type { ObjectiveSource } from '@/types/api/objective-params'
import type { Holding } from '@/types/api/risk-factor'
import type { RebalanceOptions } from '@/types/enums/sleeve-management'
import type { ValueUnit } from '@/types/enums/value-unit'
import type { Statistics } from '@/types/statistics'

export type ModelConstraintTypeValue =
  | UniverseConstraintType
  | BuyUniverseConstraintType
  | DiversificationConstraintType
  | LadderPolicyConstraintType
  | AllocationConstraintType
  | AverageValueConstraintType
  | TradeConstraintType
  | PiecewiseConstraintType
  | OrderLimitConstraintType
  | CashflowBandConstraintType

export type AssetClass = `${BondType}`

// ConfigKey enum
export enum ConfigKey {
  Type = 'type',
  Duration = 'duration',
  Issuer = 'issuer',
  FDICCode = 'fdic_code',
  GicsSector1 = 'gics_sector_1',
  GicsSector2 = 'gics_sector_2',
  GicsSector3 = 'gics_sector_3',
  GicsSector4 = 'gics_sector_4',
  Seniority = 'seniority',
  TreasurySubtype = 'treasury_subtype',
  Price = 'price',
  BondFeatures = 'bond_features',
  FilterLists = 'filter_lists',

  // Spread fields
  BenchmarkSpread = 'benchmark_spread',
  GSpread = 'g_spread',
  ZSpread = 'z_spread',
  OAS = 'oas',
  ISpread = 'i_spread',
  DTS = 'dts',

  IceSector1 = 'ice_sector_1',
  IceSector2 = 'ice_sector_2',
  IceSector3 = 'ice_sector_3',
  IceSector4 = 'ice_sector_4',
  SeniorityType = 'seniority_type',
  CountryDomicile = 'country_domicile',
  CountryIssue = 'country_issue',
  CountryOfRisk = 'country_of_risk',
  MaturityInYears = 'maturity_in_years',
  WorkoutInYears = 'workout_in_years',
  MaturityYear = 'maturity_year',
  WorkoutYear = 'workout_year',
  NextCallInYears = 'next_call_in_years',
  DaysToMaturityFromNextCall = 'days_to_maturity_from_next_call',
  CallType = 'call_type',
  CallFrequency = 'call_frequency',
  LowestRating = 'lowest_rating',
  SpRating = 'sp_rating',
  MoodysRating = 'moodys_rating',
  OutstandingAmount = 'outstanding_amount',
  IssueAmount = 'issue_amount',
  CouponValue = 'coupon_value',
  CouponType = 'coupon_type',
  CouponFrequency = 'coupon_frequency',
  GenericSector = 'generic_sector',
  UltimateBorrower = 'ultimate_borrower',
  UltimateParent = 'ultimate_parent',
  Ticker = 'ticker',
  MunicipalState = 'municipal_state',
  MuniObligorState = 'muni_obligor_state',
  MunicipalType = 'municipal_type',
  MuniSector = 'muni_sector',
  UseOfProceeds = 'use_of_proceeds',
  SpOutlook = 'sp_outlook',
  SpCreditwatch = 'sp_creditwatch',
  YieldToWorst = 'yield_to_worst',
  YieldToMaturity = 'yield_to_maturity',
  YieldToCall = 'yield_to_call',
  TradingRestrictionsType = 'trading_restrictions_type',
  SukukContractType = 'sukuk_contract_type',
  SukukType = 'sukuk_type',
  Insurer = 'insurer',
  MuniTaxStatus = 'muni_tax_status',
  CocoEventType = 'coco_event_type',
  CocoTriggerType = 'coco_trigger_type',
  CocoTriggerAction = 'coco_trigger_action',
  CocoTriggerLevelType = 'coco_trigger_level_type',
  MoodysOutlook = 'moodys_outlook',
  FloatBenchmark = 'float_benchmark',
  MakeWholeBenchmark = 'make_whole_benchmark',
  MakeWholeSpread = 'make_whole_spread',
  SpUnderlyingRating = 'sp_underlying_rating',
  MoodysUnderlyingRating = 'moodys_underlying_rating',
  EmergingStatus = 'emerging_status',
  CreditQualityClass = 'credit_quality_class',
  ImpactBondType = 'impact_bond_type',
  LiquidityInstitutionalAggregate = 'liquidity_institutional_aggregate',

  // Cashflow
  CashflowTarget = 'cashflow_target',

  // Ladder policy
  LadderPolicy = 'ladder_policy',
}

// ID types
export type ProposalID = string
export type AccountID = string
export type SleeveID = string

// Enums
export enum Priority {
  Unrelaxable = -1,
  High = 3,
  Medium = 2,
  Low = 1,
}
export const PRIORITY_OPTIONS = [
  { label: 'Low priority', value: Priority.Low },
  { label: 'Medium priority', value: Priority.Medium },
  { label: 'High priority', value: Priority.High },
  { label: 'Unrelaxable', value: Priority.Unrelaxable },
]
export enum TableView {
  Overview = 'overview',
  Universe = 'universe',
  Holdings = 'holdings',
  Actions = 'actions',
  Rules = 'rules',
}

export type ProposalBuilderMode = 'proposal' | 'model'

// Risk-factor comparison

export type BenchmarkSource =
  | 'benchmark_lqd'
  | 'benchmark_hyg'
  | 'benchmark_emb'
  | 'benchmark_mub'

export type RiskFactorSource =
  | 'universe'
  | 'existing_holdings'
  | 'proposed_holdings'
  | BenchmarkSource

export const NONE_SECONDARY = 'none' as const
export type SecondarySource = RiskFactorSource | typeof NONE_SECONDARY

export type ComparisonMode = 'absolute' | 'diff'

// What's persisted; null means "user hasn't picked yet".
export interface StoredRiskFactorComparison {
  primary: RiskFactorSource | null
  secondary: SecondarySource | null
  mode: ComparisonMode
}

// Keyed by configKey string (not the ConfigKey enum) so dynamic per-customer
// risk factors participate without existing in the sleeve yet. A local
// comparison is a complete snapshot — the factor ignores the global selection
// entirely until a global change wipes it.
export interface RiskFactorComparisonState {
  global: StoredRiskFactorComparison
  perFactor: Record<string, ResolvedRiskFactorComparison>
}

// What useRiskFactorComparison returns; nulls resolved via scenario default.
export interface ResolvedRiskFactorComparison {
  primary: RiskFactorSource
  secondary: SecondarySource
  mode: ComparisonMode
}

export const INITIAL_STORED_COMPARISON: StoredRiskFactorComparison = {
  primary: null,
  secondary: null,
  mode: 'absolute',
}

export const INITIAL_COMPARISON_STATE: RiskFactorComparisonState = {
  global: INITIAL_STORED_COMPARISON,
  perFactor: {},
}

export enum Field {
  // Bond features fields
  Convertible = 'convertible',
  ExtraordinaryCall = 'has_extraordinary_call',
  FDICInsured = 'fdic_insured',
  MakeWholeCallable = 'make_whole_callable',
  PreRefunded = 'is_pre_refunded',
  SinkingFund = 'sinking_fund',
  TraceEligible = 'trace_eligible',
  BankQualified = 'bank_qualified',
  BondWarrant = 'bond_warrant',
  Callable = 'callable',
  IsAmtApplicable = 'is_amt_applicable',
  IsStateTaxApplicable = 'is_state_tax_applicable',
  MunicipalTaxableFederal = 'municipal_taxable_federal',
  Perpetual = 'perpetual',
  PrivatePlacement = 'private_placement',
  RegS = 'reg_s',
  SukukIndicator = 'sukuk_indicator',
  SurvivorOption = 'survivor_option',

  // Conventional fields
  FDICCode = 'fdic_code',
  Duration = 'duration',
  DTS = 'duration_times_spread',
  CallType = 'call_type',
  CallFrequency = 'call_frequency',
  Coupon = 'coupon',
  CouponType = 'coupon_type',
  CouponFrequency = 'coupon_frequency',
  CountryIssue = 'country_issue',
  CountryDomicile = 'country_domicile',
  CountryOfRisk = 'country_of_risk',
  GicsSector1 = 'gics_sector_1',
  GicsSector2 = 'gics_sector_2',
  GicsSector3 = 'gics_sector_3',
  GicsSector4 = 'gics_sector_4',
  IceSector1 = 'ice_sector_1',
  IceSector2 = 'ice_sector_2',
  IceSector3 = 'ice_sector_3',
  IceSector4 = 'ice_sector_4',
  Insured = 'insured',
  IssueAmount = 'issue_size',
  Issuer = 'issuer',
  LiquidityInstitutionalAggregate = 'liquidity_institutional_aggregate',
  MaturityInYears = 'maturity_in_years',
  MaturityYear = 'maturity_year',
  WorkoutInYears = 'workout_in_years',
  WorkoutYear = 'workout_year',
  NextCallInYears = 'next_call_in_years',
  DaysToMaturityFromNextCall = 'days_to_maturity_from_next_call',
  MoodysRating = 'moodys_rating',
  LowestRating = 'lowest_rating',
  MuniSector = 'muni_sector',
  MuniObligorState = 'muni_obligor_state',
  MunicipalState = 'municipal_state',
  MunicipalType = 'municipal_type',
  OutstandingAmount = 'amount_outstanding',
  Price = 'eval_price',
  SpRating = 'sp_rating',
  Seniority = 'seniority',
  SeniorityType = 'seniority_type',
  Ticker = 'ticker',
  Type = 'type',
  TreasurySubtype = 'treasury_subtype',
  UseOfProceeds = 'use_of_proceeds',
  YieldToWorst = 'eval_ytw',
  BidYieldToWorst = 'bid_ytw',
  AskYieldToWorst = 'ask_ytw',
  YieldToMaturity = 'eval_ytm',
  BidYieldToMaturity = 'bid_ytm',
  AskYieldToMaturity = 'ask_ytm',
  MidYieldToCall = 'mid_ytc',
  BidYieldToCall = 'bid_ytc',
  AskYieldToCall = 'ask_ytc',
  SpOutlook = 'sp_outlook',
  SpCreditwatch = 'sp_creditwatch',
  TradingRestrictionsType = 'trading_restrictions_type',
  SukukContractType = 'sukuk_contract_type',
  SukukType = 'sukuk_type',
  Insurer = 'insurer',
  MuniTaxStatus = 'muni_tax_status',
  CocoEventType = 'coco_event_type',
  CocoTriggerType = 'coco_trigger_type',
  CocoTriggerAction = 'coco_trigger_action',
  CocoTriggerLevelType = 'coco_trigger_level_type',
  CocoIndicator = 'coco_indicator',
  QcbIndicator = 'qcb_indicator',
  MoodysOutlook = 'moodys_outlook',
  FloatBenchmark = 'float_benchmark',
  VrdoIndicator = 'vrdo_indicator',
  MakeWholeBenchmark = 'make_whole_benchmark',
  MakeWholeSpread = 'make_whole_spread',
  SpUnderlyingRating = 'sp_underlying_rating',
  MoodysUnderlyingRating = 'moodys_underlying_rating',
  EmergingStatus = 'emerging_status',
  CreditQualityClass = 'credit_quality_class',
  ImpactBondType = 'impact_bond_type',

  // Sector (generic)
  Sector = 'sector',

  // Global constraints fields
  Isin = 'isin',
  Cusip = 'cusip',
  Obligor = 'obligor',
  UltimateBorrower = 'ultimate_borrower',
  UltimateParent = 'ultimate_parent',

  // Benchmark Spread fields
  BidBenchmarkYTWSpreadBPS = 'bid_ytw_spread_bps',
  AskBenchmarkYTWSpreadBPS = 'ask_ytw_spread_bps',
  MidBenchmarkYTWSpreadBPS = 'mid_ytw_spread_bps',

  BidGSpreadBPS = 'bid_g_spread',
  AskGSpreadBPS = 'ask_g_spread',
  MidGSpreadBPS = 'mid_g_spread',

  BidZSpreadBPS = 'bid_z_spread',
  AskZSpreadBPS = 'ask_z_spread',
  MidZSpreadBPS = 'mid_z_spread',

  BidOAS = 'bid_oas',
  AskOAS = 'ask_oas',
  MidOAS = 'mid_oas',

  BidISpread = 'bid_i_spread',
  AskISpread = 'ask_i_spread',
  MidISpread = 'mid_i_spread',
}

export enum FieldConfigType {
  Quantitative = 'quantitative',
  Categorical = 'categorical',
}

// Field config types
export interface QuantitativeFieldConfig {
  type: FieldConfigType.Quantitative
  buckets: number[] // Default bucket boundaries, e.g., [0, 1, 2, ..., 30]
  valueUnit?: ValueUnit
  tickSize: number // Step size for input controls (e.g., 0.1 for decimal precision) - required for quantitative fields
  isDiscrete?: boolean // When true, each bucket represents a single value (e.g., maturity year 2032) and uses inclusive-max semantics like rating fields
}

export interface CategoricalFieldConfig {
  type: FieldConfigType.Categorical
  entityLabel: {
    // The description of a single entity e.g. { singular: 'issuer', plural: 'issuers' }
    singular: string
    plural: string
  }
}

// Constraint types (imported from API)
export type Constraint =
  | UniverseConstraint
  | DiversificationConstraint
  | PiecewiseConstraint
  | AverageValueConstraint
  | AllocationConstraint

export enum RiskFactorConfigType {
  Conventional = 'conventional',
  BondFeatures = 'bond_features',
  FilterList = 'filter_list',
  CashflowTarget = 'cashflow_target',
  LadderPolicy = 'ladder_policy',
}

// Risk Factor Config
export interface RiskFactorConfig {
  type: RiskFactorConfigType
  configKey: ConfigKey
  displayName: string
}

type NonEmptyArray<T> = [T, ...T[]]

export interface RatingsAccess {
  hasSPAccess: boolean | null
  hasMoodysAccess: boolean | null
}

export interface ConventionalRiskFactorConfig extends RiskFactorConfig {
  type: RiskFactorConfigType.Conventional
  fields: NonEmptyArray<FieldConfig>
  relevantAssetClasses?: AssetClass[]
}

export interface BondFeaturesRiskFactorConfig extends RiskFactorConfig {
  type: RiskFactorConfigType.BondFeatures
  fields: NonEmptyArray<BondFeatureConfig>
}

export interface FilterListRiskFactorConfig extends RiskFactorConfig {
  type: RiskFactorConfigType.FilterList
}

export interface CashflowTargetRiskFactorConfig extends RiskFactorConfig {
  type: RiskFactorConfigType.CashflowTarget
}

export interface LadderPolicyRiskFactorConfig extends RiskFactorConfig {
  type: RiskFactorConfigType.LadderPolicy
}

// Field Config
export interface FieldConfig {
  field: Field
  displayName: string
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  formatValue: (value: any) => string
  config: QuantitativeFieldConfig | CategoricalFieldConfig
}

export interface BondFeatureConfig {
  field: Field
  displayName: string
  relevantAssetClasses?: AssetClass[]
}

// Field Constraints (formerly RiskFactorConfig)
export interface FieldConstraints {
  universeConstraint?: UniverseConstraint
  diversificationConstraint?: DiversificationConstraint
  // Account-size-tiered max weight (piecewise wrapping per-tier diversification
  // constraints). Mutually exclusive with diversificationConstraint.
  piecewiseDiversificationConstraint?: PiecewiseConstraint
  averageValueConstraint?: AverageValueConstraint
  allocationConstraints: AllocationConstraint[]
}

// Filter List Constraints for FilterLists RiskFactor
export interface FilterListConstraints {
  inclusionConstraint?: UniverseConstraint // OR constraint for all included filter lists
  exclusionConstraint?: UniverseConstraint // AND constraint for all excluded filter lists
  allocationConstraints: AllocationConstraint[] // allocation constraints per filter list
}

export interface RiskFactor {
  configKey: ConfigKey
  priorityLevel: Priority
  scope: FilterObject
  fields: Partial<Record<Field, FieldConstraints>>
  filterListConstraints?: FilterListConstraints // Optional and only used for filter lists risk factor
}

// Global constraints — universe-level filters applied to all sleeves
export interface GlobalConstraints {
  bondStatusConstraint: UniverseConstraint
  structuredProductConstraint: UniverseConstraint
  currencyConstraint: UniverseConstraint
}

// Diversification parameter constraints - per-sleeve diversification settings
export interface DiversificationParameterConstraints {
  // Weight per bond (diversification on ISIN)
  weightPerBond?: DiversificationConstraint
  maxBondsPerIssuer?: DiversificationConstraint
  maxBondsPerUltimateParent?: DiversificationConstraint
  maxBondsPerUltimateBorrower?: DiversificationConstraint
  // Min/max unique count of a field value across the sleeve
  uniqueBonds?: DiversificationConstraint
  uniqueIssuers?: DiversificationConstraint
  uniqueUltimateParents?: DiversificationConstraint
  uniqueUltimateBorrowers?: DiversificationConstraint
}

export type DiversificationParameterConstraintKey =
  keyof DiversificationParameterConstraints

export enum UniqueConstraintKey {
  UniqueBonds = 'uniqueBonds',
  UniqueIssuers = 'uniqueIssuers',
  UniqueUltimateParents = 'uniqueUltimateParents',
  UniqueUltimateBorrowers = 'uniqueUltimateBorrowers',
}

// Sleeve State
export interface SleeveState {
  riskFactors: Partial<Record<ConfigKey, RiskFactor>>
  cashflowTargets: CashflowTarget[]
  cashflowTargetPriority: Priority
  rebalanceStrategy: RebalanceOptions
  otherConstraints: ModelConstraint[]
  ladderPolicy?: LadderPolicyConstraint
  /**
   * Reference date (ISO 'yyyy-MM-dd') the ladder's rung boundaries are measured
   * from. Undefined means today, snapped to the ladder's period boundary.
   */
  assignmentDate?: string
  diversificationParameterConstraints: DiversificationParameterConstraints
  objectiveTokens: EquationToken[]
  objectiveStrategy?: ObjectiveStrategy
  /**
   * Which source propman's server-side resolution produced the objective from.
   * Undefined when the objective was resolved client-side (deploy-window
   * fallback, model-builder mode). No UI consumer yet — reserved for the
   * upcoming provenance affordance.
   */
  objectiveSource?: ObjectiveSource
  globalConstraints: GlobalConstraints
  // Fraction in [0, 1] (e.g. 0.02 = 2%).
  cashBuffer: number
  // Per-sleeve minimum order increment override; null/undefined = fall back to the model, then account-level.
  minimum_order_increment?: number | null
  // The assigned model's strategy-level increment, hydrated read-only for
  // display fallback; never saved as a sleeve override.
  modelMinimumOrderIncrement?: number | null
  // No builder control; carried so a save does not reset it to the backend default.
  scopeRelativeWeights?: boolean
}

// Sleeve
export interface Sleeve {
  sleeveId: SleeveID
  name: string // Sleeve name
  modelId?: string // Model ID
  modelName?: string // Model name
  minAllocation: number
  targetAllocation: number // Target allocation (market value in dollars)
  maxAllocation: number
  state: SleeveState
}

export interface SleeveCompliance {
  before: ComplianceCheck[]
  after: ComplianceCheck[]
}

export interface SleeveStatistics {
  before: Statistics | null
  after: Statistics | null
}

export interface AccountStatistics {
  before: Statistics | null
  after: Statistics | null
}

// Investor Information (extended from API Investor type)
export interface InvestorInfo {
  investor_name: string
  // country_of_residence: string // Not stored in API
  state_of_residence: string
  currency: string
  federal_income_tax: number // stored as decimal (e.g., 0.40 for 40%)
  state_income_tax: number // stored as decimal
  total_investment: number // stored as dollar amount
  // Retained for form schema compatibility; no longer rendered at account level.
  minimum_order_increment: string
  minimum_quantity: number | null
  // Backend tax profile classification. Undefined for the frontend "None"
  // state, where the user has explicitly chosen no profile — distinct from
  // an explicit "us_taxable" choice with zero rates.
  tax_profile?: TaxProfileValue
  custom_income_tax: number // stored as decimal (e.g., 0.40 for 40%)
  custom_capital_gains_tax: number // stored as decimal
}

export const DEFAULT_INVESTMENT_AMOUNT = 10_000_000

// Note: Tax rates default to 0 for international investors (PORT-796)
export const DEFAULT_INVESTOR_INFO: InvestorInfo = {
  investor_name: '',
  state_of_residence: '',
  currency: 'USD',
  federal_income_tax: 0,
  state_income_tax: 0,
  total_investment: DEFAULT_INVESTMENT_AMOUNT,
  minimum_order_increment: '',
  minimum_quantity: null,
  custom_income_tax: 0,
  custom_capital_gains_tax: 0,
}

// Statistics banner stat IDs
export enum StatId {
  // Eligible-universe counts (from live inventory, not proposal snapshots)
  EligibleBonds = 'eligible_bonds',
  EligibleIssuers = 'eligible_issuers',
  EligibleUltimateParents = 'eligible_ultimate_parents',

  // Order characteristics
  OrderMV = 'order_mv',
  PortfolioMarketValue = 'portfolio_market_value',
  Cash = 'cash',
  NumTrades = 'num_trades',
  NumIssuers = 'num_issuers',
  NumSecurities = 'num_securities',
  AvgLimitYTW = 'avg_limit_ytw',
  AvgLimitYTM = 'avg_limit_ytm',
  AvgLimitTEY = 'avg_limit_tey',
  AvgPostTaxLimitYTW = 'avg_post_tax_limit_ytw',
  AvgWeightPerBond = 'avg_weight_per_bond',

  // Risk factor averages (from after statistics)
  AvgEvalYTW = 'avg_eval_ytw',
  AvgEvalYTM = 'avg_eval_ytm',
  // Keeps the pre-split 'avg_tey' id so persisted stat configs still resolve.
  AvgEvalTEY = 'avg_tey',
  AvgRating = 'avg_rating',
  AvgDuration = 'avg_duration',
  AvgMaturity = 'avg_maturity',
  AvgCoupon = 'avg_coupon',
  AvgZSpread = 'avg_z_spread',
}

// Config for risk factor display on the grid.
export interface RiskFactorDisplayConfig {
  hidden: Set<string>
  sorting: string[]
}

// Store flags for tracking state changes
interface ProposalBuilderStoreFlags {
  dirtyCount: number
}

// Proposal metadata (persisted with proposal, loaded from/sent to API)
type ProposalMetadataFlags = Record<string, unknown>

export enum ProposalMode {
  Rebalancing = 'rebalancing',
  NewMoney = 'new_money',
}

export interface ProposalMetadata {
  source?: 'proposal_builder' | 'model_builder'
  flags: ProposalMetadataFlags
  last_optimization_risk_group_id?: string
  mode?: ProposalMode
  initial_account_cash?: Record<string, number>
}

// Whether the builder shows the whole account or a single sleeve. Derived
// from the proposal-store's root `activeScope` (account id = account view).
export type ProposalBuilderView = 'account' | 'sleeve'

// The store mixes server-loaded actions (fully stamped) with optimistic mirrors
// written before the round-trip, which have no created_at/created_by yet.
type OptimisticProposalAction = Omit<
  ProposalAction,
  'created_at' | 'created_by'
> &
  Partial<Pick<ProposalAction, 'created_at' | 'created_by'>>

// Main State
export interface ProposalBuilderState {
  // Identity of the single account this proposal operates on — not a
  // selection (that's the store-root activeScope).
  activeAccount: AccountID

  // Proposal data state
  sleeves: Sleeve[]
  orders: ProposalOrder[]
  proposalId: ProposalID
  proposalName: string
  // True only when the API explicitly reports `synthetic: false` — i.e. the
  // proposal is linked to a real account, not a synthetic (new-client /
  // model-builder backing) proposal. Drives naming behavior: linked
  // proposals keep their account-derived name instead of inheriting a
  // template's. Requiring the explicit flag means an absent field degrades
  // to legacy behavior (template rename applies) rather than guessing.
  isAccountLinked: boolean
  optimizationId: string | null
  version: number
  proposalStatus: ProposalStatus
  accountBefore: AccountDetail | null
  accountAfter: AccountDetail | null
  holdingsBefore: Record<SleeveID | AccountID, Holding[]>
  holdingsAfter: Record<SleeveID | AccountID, Holding[]>
  holdingsHeld: Record<SleeveID | AccountID, Holding[]>
  proposalActions: OptimisticProposalAction[]
  sleeveStatistics: Record<SleeveID, SleeveStatistics>
  accountStatistics: AccountStatistics
  sleeveCompliance: Record<SleeveID, SleeveCompliance>
  constraintRelaxation: ConstraintRelaxation
  metadata: ProposalMetadata
  // Server-owned: pinned by propman on creation and immutable afterwards, so
  // it sits outside `metadata`, which round-trips back to propman on save.
  valuationBasis?: NonNullable<ValuationConfigBasis>

  // Store flags
  flags: ProposalBuilderStoreFlags
}

export interface SaveResult {
  success: boolean
  errors: string[]
  // Propman refused the overwrite: the proposal is no longer editable.
  conflict: boolean
}

// Context value — provider-level functions and universeFilters
export interface ProposalBuilderContextValue {
  mode: ProposalBuilderMode
  modelId: string | null
  universeFilters: FilterObject
  optimize: (overrideState?: ProposalBuilderState) => Promise<void>
  refreshProposal: () => void
  saveRiskFactorConfig: (config: RiskFactorDisplayConfig) => void
  applyTemplate: (modelId: string) => Promise<void>
  isOptimizing: boolean
  isSaving: boolean
  // Hosted here so the Actions table, table modal, and CSV export all see the
  // same instance — `useColumnConfig` keeps state in local React state, so
  // sibling callers would otherwise desync until reload.
  actionsColumnConfig: UseProposalBuilderColumnConfigReturn
  actionsCsvColumnConfig: UseProposalBuilderColumnConfigReturn
  actionsSort: ReturnType<typeof useProposalTableState>
  actionsCsvSort: ReturnType<typeof useProposalTableState>
}
